import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Wallet, Zap, X, Loader2 } from 'lucide-react';
import {
  redeemPoints,
  fetchRedemptionConfig,
  fetchWallet,
  type RedeemResult,
} from '@/lib/api';
import NamedIcon from '@/components/ui/NamedIcon';
import styles from './RedemptionSheet.module.css';

/**
 * CSS-only confetti shower fired on successful redemption.
 *
 * 40 particles per burst with randomized horizontal-drift end
 * positions, rotation, animation duration, and start delay. Each
 * randomization parameter is written as a CSS custom property
 * on the particle element — the keyframe in the stylesheet
 * consumes them via `var()`. No JS animation loop, no new
 * dependency.
 *
 * Colors pulled from a brand-friendly five-stop palette: accent
 * gold, brand green, warm sun, sky blue, soft pink. Each
 * particle picks one at random per render.
 *
 * Honors prefers-reduced-motion via the CSS rule that sets
 * .confettiLayer { display: none }.
 */
const CONFETTI_COLORS = ['#FACC15', '#22C55E', '#FB923C', '#60A5FA', '#F472B6'];
const CONFETTI_COUNT = 40;

function Confetti(): JSX.Element {
  // Particles are deterministic per mount via useMemo so React's
  // strict-mode double-render in dev doesn't double-randomize and
  // visibly stutter. Component remounts (i.e., a second redemption)
  // fire a fresh batch.
  const particles = useMemo(() => {
    return Array.from({ length: CONFETTI_COUNT }, (_, i) => {
      const left = Math.random() * 100;             // start x in vw
      const xDrift = (Math.random() - 0.5) * 40;    // -20 to +20 vw drift
      const rot = (Math.random() - 0.5) * 1080;     // -540 to +540 deg
      const dur = 2 + Math.random() * 1.2;          // 2 to 3.2s
      const delay = Math.random() * 0.6;            // 0 to 0.6s stagger
      const bg = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
      return { left, xDrift, rot, dur, delay, bg, key: i };
    });
  }, []);

  return (
    <div className={styles.confettiLayer} aria-hidden>
      {particles.map((p) => (
        <span
          key={p.key}
          className={styles.confettiPiece}
          style={{
            // CSS custom properties consumed by the keyframe.
            // Cast through Record so React accepts the --prefixed
            // keys without complaining about CSSProperties typing.
            ['--left' as string]: String(p.left),
            ['--x' as string]: String(p.xDrift),
            ['--rot' as string]: `${p.rot}deg`,
            ['--dur' as string]: `${p.dur}s`,
            ['--delay' as string]: `${p.delay}s`,
            ['--bg' as string]: p.bg,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Bottom-sheet redemption flow.
 *
 * Two targets:
 *   - `wallet`: cents land in the WalletModel balance immediately
 *   - `session_credit`: cents queue on the user's gamification doc
 *     and consume on the NEXT session-completion (carry over if not
 *     fully spent)
 *
 * Idempotency: a UUID is generated on mount and re-sent with every
 * confirm attempt so a flaky network retry collapses to one ledger
 * write. Server returns `idempotent: true` on dedupe; the sheet
 * surfaces that as a non-error.
 *
 * Portal target = document.body. Required because the /rewards
 * route lives inside AppShell's `.pageTransition` wrapper whose
 * `evi-page-enter` keyframe applies a transform — that transform
 * promotes the wrapper to a CSS containing block for `position:fixed`
 * descendants, anchoring this sheet's `inset: 0` backdrop to the
 * wrapper instead of the viewport. Result before the portal fix:
 * the Redeem button no-op'd because the sheet rendered off-position
 * and was never visible. Same trap (and same fix) `BottomSheet.tsx`
 * documents for the Profile sheets.
 */

interface RedemptionSheetProps {
  totalPoints: number;
  onClose: () => void;
  /** Fired after a successful redemption so the parent can invalidate
   *  the gamification + wallet queries. */
  onRedeemed: (result: RedeemResult) => void;
}

export function RedemptionSheet({ totalPoints, onClose, onRedeemed }: RedemptionSheetProps) {
  const { t } = useTranslation('rewardsExtra');
  const qc = useQueryClient();
  const configQ = useQuery({
    queryKey: ['gamification', 'redemption-config'],
    queryFn: fetchRedemptionConfig,
    staleTime: 60_000,
  });
  const walletQ = useQuery({
    queryKey: ['gamification', 'wallet'],
    queryFn: fetchWallet,
    staleTime: 30_000,
  });

  const [target, setTarget] = useState<'wallet' | 'session_credit'>('wallet');
  const [points, setPoints] = useState(0);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<RedeemResult | null>(null);

  // Idempotency token — stable for the lifetime of this sheet so a
  // confirm-retry after a network blip dedupes server-side.
  const idemRef = useRef<string>(
    `redeem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
  );

  const config = configQ.data;
  const wallet = walletQ.data;

  // Initialise the points slider to the minimum once we know it.
  useEffect(() => {
    if (config && points === 0) {
      setPoints(Math.min(config.minRedeemablePoints, totalPoints));
    }
  }, [config, points, totalPoints]);

  const min = config?.minRedeemablePoints ?? 100;
  const ppd = config?.pointsPerDollar ?? 100;
  const dollars = points / ppd;
  const valid = config != null && points >= min && points <= totalPoints;

  const quickAmounts = useMemo(() => {
    const opts = [min, min * 2, min * 5, min * 10].filter((n) => n <= totalPoints);
    return Array.from(new Set(opts));
  }, [min, totalPoints]);

  const handleConfirm = async () => {
    if (!valid) return;
    setPending(true);
    setError(null);
    try {
      const res = await redeemPoints({ points, target }, idemRef.current);
      setSuccess(res);
      qc.invalidateQueries({ queryKey: ['gamification'] });
      onRedeemed(res);
    } catch (err: unknown) {
      // ky throws HTTPError on non-2xx; pull the JSON body if there is one.
      const httpErr = err as { response?: Response; message?: string };
      let msg = httpErr.message ?? t('redemptionFailed');
      if (httpErr.response) {
        try {
          const j = await httpErr.response.clone().json() as
            { error?: { message?: string } | string; hint?: string };
          msg = typeof j.error === 'string' ? j.error : (j.error?.message ?? msg);
          if (j.hint) msg += ` ${j.hint}`;
        } catch { /* fall through */ }
      }
      setError(msg);
    } finally {
      setPending(false);
    }
  };

  // SSR / pre-hydration guard. Vite builds client-only today but
  // the typeof check keeps the component portable.
  if (typeof document === 'undefined') return null;

  const sheetTree = (
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label={t('dialogLabel')}
      onClick={onClose}
    >
      {/* Confetti renders once on success and unmounts when the
          sheet closes (~3.2s of animation, then the layer's
          children have all completed). Sits above the sheet via
          z-index 65 so particles pass over the surface. */}
      {success && <Confetti />}
      <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
        <div className={styles.handle} aria-hidden />
        <div className={styles.header}>
          <h2 className={styles.title}>{t('title')}</h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label={t('close')}
          >
            <X size={16} />
          </button>
        </div>

        {success ? (
          <SuccessCard result={success} onClose={onClose} />
        ) : (
          <>
            {/* Available */}
            <div className={styles.balanceBlock}>
              <span className={styles.balanceLabel}>{t('available')}</span>
              <span className={styles.balanceValue}>
                {totalPoints.toLocaleString()}
              </span>
              <span className={styles.balanceUnit}>{t('pointsUnit')}</span>
              {wallet && (
                <span className={styles.walletLine}>
                  {t('walletBalance', { amount: `$${(wallet.balanceCents / 100).toFixed(2)}` })}
                  {wallet.pendingSessionCreditCents > 0 && (
                    <> {t('pendingSessionCredit', { amount: `$${(wallet.pendingSessionCreditCents / 100).toFixed(2)}` })}</>
                  )}
                </span>
              )}
            </div>

            {/* Quick select */}
            <div className={styles.quickRow}>
              {quickAmounts.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPoints(n)}
                  className={`${styles.quickBtn} ${points === n ? styles.quickBtnActive : ''}`}
                >
                  {n.toLocaleString()}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPoints(totalPoints)}
                className={`${styles.quickBtn} ${points === totalPoints ? styles.quickBtnActive : ''}`}
              >
                {t('all')}
              </button>
            </div>

            {/* Custom input */}
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('amountLabel')}</span>
              <input
                type="number"
                inputMode="numeric"
                className={styles.amountInput}
                value={points}
                min={min}
                max={totalPoints}
                onChange={(e) => {
                  const n = Math.max(0, Math.min(totalPoints, Math.floor(Number(e.target.value) || 0)));
                  setPoints(n);
                }}
              />
            </label>

            {/* Target choice */}
            <div className={styles.targetRow}>
              <button
                type="button"
                onClick={() => setTarget('wallet')}
                className={`${styles.targetBtn} ${target === 'wallet' ? styles.targetBtnActive : ''}`}
              >
                <Wallet size={14} />
                <span className={styles.targetLabel}>{t('target.walletLabel')}</span>
                <span className={styles.targetDesc}>{t('target.walletDesc')}</span>
              </button>
              <button
                type="button"
                onClick={() => setTarget('session_credit')}
                className={`${styles.targetBtn} ${target === 'session_credit' ? styles.targetBtnActive : ''}`}
              >
                <Zap size={14} />
                <span className={styles.targetLabel}>{t('target.sessionLabel')}</span>
                <span className={styles.targetDesc}>{t('target.sessionDesc')}</span>
              </button>
            </div>

            {/* Conversion preview */}
            <div className={styles.preview}>
              <div>
                <span className={styles.previewLabel}>{t('preview.receive')}</span>
                <span className={styles.previewValue}>${dollars.toFixed(2)}</span>
              </div>
              <span className={styles.previewRate}>
                {t('preview.rate', { rate: ppd.toLocaleString() })}
              </span>
            </div>

            {error && <div className={styles.error}>{error}</div>}

            <button
              type="button"
              className={styles.confirmBtn}
              onClick={handleConfirm}
              disabled={!valid || pending}
            >
              {pending
                ? <><Loader2 size={14} className={styles.spinner} /> {t('redeeming')}</>
                : t('redeemCta', { points: points.toLocaleString() })}
            </button>
            <p className={styles.smallprint}>
              {t('smallprint', { min: min.toLocaleString() })}
            </p>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(sheetTree, document.body);
}

function SuccessCard({ result, onClose }: { result: RedeemResult; onClose: () => void }) {
  const { t } = useTranslation('rewardsExtra');
  const amount = `$${result.dollarsCredited.toFixed(2)}`;
  return (
    <div className={styles.successBlock}>
      <div className={styles.successIcon}>
        {/* Glyph bumped 26 → 40 paired with the .successIcon
            container's 56 → 80 bump — proportional scaling so the
            glyph keeps the same visual weight inside the larger
            pill. Lucide Wallet for wallet-target redemptions, Zap
            for session-credit-target. */}
        {result.target === 'wallet'
          ? <NamedIcon name="wallet" fallback={<Wallet size={40} />} size={40} />
          : <NamedIcon name="bolt" fallback={<Zap size={40} />} size={40} />}
      </div>
      <h3 className={styles.successTitle}>
        {result.target === 'wallet'
          ? t('success.addedToWallet', { amount })
          : t('success.queuedForSession', { amount })}
      </h3>
      <p className={styles.successDesc}>
        {result.target === 'wallet'
          ? t('success.walletDesc')
          : t('success.sessionDesc')}
        {result.idempotent && t('success.idempotent')}
      </p>
      {result.newPointsBalance !== undefined && (
        <p className={styles.successBalance}>
          {t('success.newBalanceLabel')}: <strong>{result.newPointsBalance.toLocaleString()}</strong> pts
        </p>
      )}
      <button type="button" className={styles.confirmBtn} onClick={onClose}>
        {t('success.done')}
      </button>
    </div>
  );
}
